import { useCallback, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { groupBy, sortBy } from 'lodash'
import { findAllWithSubjectsTabs } from '__generatedTypes__/findAllWithSubjectsTabs'
import { GET_PLATFORMS_WITH_SUBJECTS } from 'queries/subject'
import { findAllQuestionCategory, findAllQuestionCategoryVariables } from '__generatedTypes__/findAllQuestionCategory'
import { GET_LIST_QUESTION_CATEGORY } from 'queries/questionCategory'
import { QuestionTypeEnum, SubjectEnumType } from '__generatedTypes__/globalTypes'
import {
  CARSBoosterBaseSubject,
  CasperBoosterBaseSubject,
  INBDEBoosterBaseSubject,
  NBDHEBoosterBaseSubject,
  PlatformSlug,
  TAKE_ALL_ITEMS_COUNT,
} from 'constants/consts'
import {
  BasePlatform,
  Platform,
  Subject,
  SubjectGroup,
  QuestionCategory,
  PHARMACOLOGY_SUBJECT_SLUG,
  PLATFORMS_FOR_TYPES,
  QUESTION_TYPES,
  QuestionType,
} from 'pagesNew/Questions/Form/consts'

export type { Platform, Subject, SubjectGroup, QuestionCategory } from 'pagesNew/Questions/Form/consts'

const SINGLE_SUBJECT_PLATFORMS = [
  PlatformSlug.CARSBooster,
  PlatformSlug.CasperBooster,
  PlatformSlug.INBDEBooster,
  PlatformSlug.NBDHEBooster,
  PlatformSlug.NDEBCrusher,
  // Not implemented - separate subjects for now (not to break existing question categories)
  PlatformSlug.MCATBooster,
  PlatformSlug.NBEOPartOne,
  PlatformSlug.DLOSCE,
  PlatformSlug.GREBooster,
]

export const AVAILABLE_HIDDEN_SUBJECTS: Subject[] = [
  {
    id: CARSBoosterBaseSubject,
    tabName: 'CARS',
    slug: 'cars',
    platformSlug: PlatformSlug.CARSBooster,
    isSingle: SINGLE_SUBJECT_PLATFORMS.includes(PlatformSlug.CARSBooster),
  },
  {
    id: CasperBoosterBaseSubject,
    tabName: 'Question Banks',
    slug: 'question-banks',
    platformSlug: PlatformSlug.CasperBooster,
    isSingle: SINGLE_SUBJECT_PLATFORMS.includes(PlatformSlug.CasperBooster),
  },
  {
    id: INBDEBoosterBaseSubject,
    tabName: 'Case-Based Scenarios',
    slug: 'case-based-scenarios',
    platformSlug: PlatformSlug.INBDEBooster,
    isSingle: SINGLE_SUBJECT_PLATFORMS.includes(PlatformSlug.INBDEBooster),
  },
  {
    id: NBDHEBoosterBaseSubject,
    tabName: 'Case-Based Scenarios',
    slug: 'case-based-scenarios',
    platformSlug: PlatformSlug.NBDHEBooster,
    isSingle: SINGLE_SUBJECT_PLATFORMS.includes(PlatformSlug.NBDHEBooster),
  },
]

const getPlatformPrefix = (platformSlug: PlatformSlug) => {
  switch (platformSlug) {
    case PlatformSlug.CARSBooster:
      return 'CARS'
    case PlatformSlug.CasperBooster:
      return 'Casper'
    case PlatformSlug.NDEBCrusher:
      return 'V.OSCE'
    case PlatformSlug.NBEOPartOne:
      return 'NBEO® Part 1 Prep'
  }

  return platformSlug.toUpperCase()
}

export const useQuestionHelpers = () => {
  const { data: platformsData, loading: platformsLoading } =
    useQuery<findAllWithSubjectsTabs>(GET_PLATFORMS_WITH_SUBJECTS)
  const { data: questionCategoriesData, loading: questionCategoriesLoading } = useQuery<
    findAllQuestionCategory,
    findAllQuestionCategoryVariables
  >(GET_LIST_QUESTION_CATEGORY, {
    variables: {
      params: {
        take: TAKE_ALL_ITEMS_COUNT,
      },
    },
    // we should always have fresh question categories list
    fetchPolicy: 'network-only',
  })

  const getAvailableQuestionTypes = useCallback((activePlatform?: PlatformSlug): QuestionType[] => {
    if (!activePlatform) {
      return QUESTION_TYPES
    }

    return QUESTION_TYPES.filter((questionType) => PLATFORMS_FOR_TYPES[questionType.id].includes(activePlatform))
  }, [])

  const allPlatforms: BasePlatform[] = useMemo(
    () =>
      (platformsData?.findAllWithSubjectsTabs ?? []).filter((platform) => platform.slug !== PlatformSlug.Admissions),
    [platformsData?.findAllWithSubjectsTabs]
  )

  const getAvailablePlatforms = useCallback(
    (selectedQuestionType?: QuestionTypeEnum): Platform[] => {
      if (!selectedQuestionType) {
        return allPlatforms
      }

      return allPlatforms.filter((platform) =>
        PLATFORMS_FOR_TYPES[selectedQuestionType].includes(platform.slug as PlatformSlug)
      )
    },
    [allPlatforms]
  )

  const allSubjects: Subject[] = useMemo(
    () => [
      ...AVAILABLE_HIDDEN_SUBJECTS,
      ...allPlatforms.flatMap((platform) =>
        platform.subjects
          .filter((subject) => subject.type === SubjectEnumType.Regular)
          .map((subject) => ({
            id: subject.id,
            tabName: subject.tabName,
            slug: subject.slug,
            platformSlug: platform.slug as PlatformSlug,
            isSingle: SINGLE_SUBJECT_PLATFORMS.includes(platform.slug as PlatformSlug),
          }))
      ),
    ],
    [allPlatforms]
  )

  const allSubjectGroups: SubjectGroup[] = useMemo(() => {
    const singleSubjectGroups: SubjectGroup[] = allSubjects
      .filter((subject) => subject.isSingle)
      .map((subject) => ({
        id: `${subject.platformSlug.toUpperCase()}-${subject.slug}`,
        slug: subject.slug,
        title: `${getPlatformPrefix(subject.platformSlug)} - ${subject.tabName}`,
        subjects: [subject],
        isSingle: true,
      }))

    const multiSubjectGroups: SubjectGroup[] = Object.entries(
      groupBy(
        allSubjects.filter((subject) => !subject.isSingle),
        'tabName'
      )
    ).map(([title, subjects]) => ({
      id: `${subjects.map((subject) => subject.platformSlug.toUpperCase()).join('-')}-${title}`,
      slug: subjects[0].slug,
      title,
      subjects,
      isSingle: false,
    }))

    return [...sortBy(multiSubjectGroups, 'title'), ...sortBy(singleSubjectGroups, 'title')]
  }, [allSubjects])

  const getAvailableSubjectGroups = useCallback(
    (
      selectedPlatforms?: PlatformSlug[],
      selectedQuestionType?: QuestionTypeEnum,
      isOnlySelectedPlatformsSubjects?: boolean
    ): SubjectGroup[] => {
      // Find common subject groups for selected platforms (group should have subjects for all selected platforms)
      const filteredByPlatforms = selectedPlatforms
        ? allSubjectGroups
            .filter((group) =>
              selectedPlatforms.every((platformSlug) =>
                group.subjects.some((subject) => subject.platformSlug === platformSlug)
              )
            )
            .map((group) =>
              isOnlySelectedPlatformsSubjects
                ? {
                    ...group,
                    subjects: group.subjects.filter((subject) => selectedPlatforms.includes(subject.platformSlug)),
                  }
                : group
            )
        : allSubjectGroups

      if (selectedQuestionType) {
        // Here we can setup a custom logic for some question type
        if (selectedQuestionType === QuestionTypeEnum.RxWritingNDEB) {
          return filteredByPlatforms.filter((group) =>
            group.subjects.find(({ slug }) => slug === PHARMACOLOGY_SUBJECT_SLUG)
          )
        }
      }

      return filteredByPlatforms
    },
    [allSubjectGroups]
  )

  const allQuestionCategories: QuestionCategory[] = useMemo(
    () => questionCategoriesData?.findAllQuestionCategory.items ?? [],
    [questionCategoriesData?.findAllQuestionCategory.items]
  )

  const getAvailableQuestionCategories = useCallback(
    (selectedSubjectIds?: string[], searchString?: string): QuestionCategory[] => {
      // Find common question categories for selected subjects (category should have subjects for all selected subjects)
      const filteredBySubjects = selectedSubjectIds
        ? allQuestionCategories.filter((category) =>
            selectedSubjectIds.every((subjectId) => category.subjects?.some((subject) => subject.id === subjectId))
          )
        : allQuestionCategories

      const filteredBySearchString = searchString
        ? filteredBySubjects.filter((category) => category.title.toLowerCase().includes(searchString.toLowerCase()))
        : filteredBySubjects

      return filteredBySearchString
    },
    [allQuestionCategories]
  )

  return {
    loading: platformsLoading || questionCategoriesLoading,
    allQuestionType: QUESTION_TYPES,
    allPlatforms,
    allSubjects,
    allSubjectGroups,
    allQuestionCategories,
    getAvailableQuestionTypes,
    getAvailablePlatforms,
    getAvailableSubjectGroups,
    getAvailableQuestionCategories,
  }
}
